import React from 'react';
import { Link } from 'react-router-dom';
import { AdjustmentTypeCell, StatusPill } from './PriceTable';
import { reportStyle } from '../../../shared/ReportShell';
import FieldHelp from '../../../shared/FieldHelp';
import { basisOf, priceLine } from '../../../shared/pricingBasis';
import { viewUnitPrice } from '../../../shared/gstView';
import '../../../shared/StyledInput.css';
import { formatDate } from '../../../shared/tenantClock';
import { formatMoney, formatNumber, formatPercent } from '../../../shared/numberFormat';
import { addressLines } from '../../../shared/addressFormat';

// Shared between PriceLookupPage's live view and PriceLookupReportView's
// print/PDF view (productReportShared.js's ProductDetailBlock is the same
// idea applied here) — one render of a resolved row, used by both, so the
// printed report can never drift out of sync with what's on screen.

export function productLabel(p) {
  const base = `${p.sku} — ${p.name}`;
  return p.supplier_sku ? `${base} (Supplier SKU: ${p.supplier_sku})` : base;
}

export function customerLabel(c) {
  return c.name;
}

// Same line1/line2 + suburb/state/postcode shape CommercialEntityDetailPage
// shows for an address, flattened to one line. Exported on its own (not
// just folded into addressLabel below) so AddressPicker.js can give the
// Address Type its own visual weight in a two-line list item instead of
// concatenating it into the same run-on string a native <select> option
// was stuck with.
// Parts follow the Address's country's order (#395).
export function addressBody(a) {
  return [...addressLines(a), a.country].filter(Boolean).join(', ');
}

// Single-line "Type — body" form, for anywhere a flat string is enough
// (AddressPicker's own closed/trigger state, the printed report).
export function addressLabel(a) {
  const body = addressBody(a);
  return a.address_type?.name ? `${a.address_type.name} — ${body}` : body;
}

export function groupLabel(g) {
  return g.short_code ? `${g.short_code} — ${g.name}` : g.name;
}

// '—' for blank, otherwise shared/numberFormat.js's company style.
export function formatPrice(price) {
  return formatMoney(price) || '—';
}

// A line's unit price: 2 decimals, or up to 4 when it has them (a price
// re-keyed from a migrated order, e.g. $5.5682) — never rounded away.
export function formatUnitPrice(price) {
  if (price === null || price === undefined || price === '') return '—';
  return formatMoney(price, { decimals: unitPriceDecimals(price) }) || '—';
}

export function unitPriceDecimals(price) {
  const n = Number(price);
  if (Number(n.toFixed(2)) === Number(n.toFixed(4))) return 2;
  return Number(n.toFixed(3)) === Number(n.toFixed(4)) ? 3 : 4;
}

export function formatMarginPercent(pct) {
  return formatPercent(pct, 1) || '—';
}

export function formatQty(qty) {
  return formatNumber(qty, { maxDecimals: 2 }) || '—';
}

// A resolved row's figures in the Ex GST or Inc GST view. Ex is the
// resolved price as stored. Inc is worked out the way an order line in the
// scope's Pricing Basis would (shared/pricingBasis.js): Retail's own price
// is its stored, rounded inc price; any other price under a Retail basis
// is grossed up and rounded to that basis's rounding; under Business (or
// no basis to go by) it's the ex price grossed up without rounding.
export function viewFigures(row, { qty, inc, taxHandling }) {
  const perBase = (unit) => (unit === null || unit === undefined || !row.baseQty ? null : unit / (row.baseQty / Number(qty || 1)));
  if (!inc) {
    return {
      unitPrice: row.unitPrice,
      extendedPrice: row.extendedPrice,
      basePrice: row.basePrice,
      nextBreakUnitPrice: row.nextBreak?.unitPrice ?? null,
      nextBreakExtendedPrice: row.nextBreak?.extendedPrice ?? null,
    };
  }
  const hasRetailInc = row.unitPriceInc !== null && row.unitPriceInc !== undefined;
  const retail = taxHandling?.pricingBasis === 'retail' || hasRetailInc;
  const basis = { ...basisOf({ pricing_basis: 'retail', retail_rounding_cents: taxHandling?.retailRoundingCents }), retail };
  const priceAt = (unitPriceEx, unitPriceInc, lineQty) => {
    const line = priceLine({ unitPriceEx, unitPriceInc, qty: lineQty, gstRate: row.gstRate, basis });
    return { ...line, unitPriceInc: retail ? line.unitPriceInc : viewUnitPrice({ unitPrice: unitPriceEx }, row.gstRate, true) };
  };
  const line = priceAt(row.unitPrice, row.unitPriceInc, qty);
  const nextBreak = row.nextBreak ? priceAt(row.nextBreak.unitPrice, null, row.nextBreak.minQuantity) : null;
  return {
    unitPrice: line.unitPriceInc,
    extendedPrice: line.extendedPriceIncGst,
    gstAmount: line.gstAmount,
    basePrice: perBase(line.unitPriceInc),
    nextBreakUnitPrice: nextBreak?.unitPriceInc ?? null,
    nextBreakExtendedPrice: nextBreak?.extendedPriceIncGst ?? null,
  };
}

const ROUNDING_DIRECTION_TEXT = { nearest: 'nearest', up: 'up to the nearest', down: 'down to the nearest' };

function formatRoundingPhrase(increment, direction) {
  const incrementLabel = increment === 1 ? '1 cent' : `${increment} cents`;
  const directionText = ROUNDING_DIRECTION_TEXT[direction] || direction;
  return direction === 'nearest' ? `rounded to the ${directionText} ${incrementLabel}` : `rounded ${directionText} ${incrementLabel}`;
}

// Shown only for a Rule-derived (Calculation) winner — a Fixed Override
// price is a hand-typed figure with nothing to derive, so it gets no
// breakdown box (see priceResolution.js's applyPriceAtQuantity, which
// returns breakdown: null for that case).
export function CalculationBreakdown({ breakdown, anchorName, scopeLabel, incPrice = null }) {
  if (!breakdown) return null;
  const discountLabel = breakdown.ruleType === 'percent_off'
    ? `${formatPercent(breakdown.amount)} off`
    : `${formatPrice(breakdown.amount)} off`;
  const roundingPhrase = formatRoundingPhrase(breakdown.roundingIncrement, breakdown.roundingDirection);
  // Names which Customer Scope this result actually belongs to before the
  // anchor it's calculated off — "Dealer = Retail Price $X..." — since the
  // winning Price's own scope (Dealer) and the fixed calculation basis
  // (Retail) are two different things and the bare "Retail Price" line
  // read as if Retail's own price were what was being shown. Omitted when
  // there's nothing more specific than the anchor itself to name.
  const scopePrefix = scopeLabel && scopeLabel !== anchorName ? `${scopeLabel} = ` : '';
  // The Rule's Basis (priceResolution.js's ruleBasisPrice) — Base Price x
  // this UoM's quantity, or Retail's own price for the UoM.
  const basisLabel = breakdown.basis === 'retail_price' ? `${anchorName} Price` : 'Base Price × Qty';
  return (
    <div style={{ background: '#f3f6fb', border: '1px solid #dce3ee', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#3b4a5e', marginBottom: 10 }}>
      Calculation: {scopePrefix}{basisLabel} {formatPrice(breakdown.retailPrice)} &minus; {discountLabel} = {formatPrice(breakdown.preRoundedPrice)}, {roundingPhrase} &rarr; {formatPrice(breakdown.price)} (Ex GST)
      {/* The Rule works on ex GST figures, so its steps stay ex; the Inc
          GST view adds where that lands inc GST. */}
      {incPrice !== null && incPrice !== undefined && <> = {formatUnitPrice(incPrice)} (Inc GST)</>}
    </div>
  );
}

export function toAdjTypeItem(method) {
  if (method.kind === 'rule') {
    return { rule: { name: method.name, type: method.type, tiers: method.tiers } };
  }
  return { rule: null, breaks: method.breaks };
}

// SKU only, not the full "SKU — Name (Supplier SKU: ...)" productLabel string
// productSearch holds — the Price Resolution Priority table is a dense
// candidates grid, not the header area where the full description already
// appears once.
export function describeProductScope(candidate, { productSearch }) {
  if (!candidate.product_id) return 'All Products';
  return productSearch ? productSearch.split(' — ')[0] : productSearch;
}

export function describeCustomerScope(candidate, { customerSearch, customerGroups }) {
  if (candidate.customer_id) return customerSearch || 'Customer';
  if (candidate.customer_price_group_id) {
    const group = customerGroups.find((g) => String(g.id) === String(candidate.customer_price_group_id));
    return group ? groupLabel(group) : 'Customer Price Group';
  }
  return 'All Customers';
}

// An exact-Customer match already outranks any Customer Price Group row
// could ever be — those unevaluated rows genuinely can't change the result,
// so the caveat would be noise, not a real gap, once that's true.
export function showCustomerGroupSkipNote(customerScopeType, result) {
  return customerScopeType !== 'group'
    && result?.customerPriceGroupSkippedCount > 0
    && !result?.rows[0]?.winnerIsExactCustomerMatch;
}

// Same pill spec as StatusPill/PriceTable.js's Current/Future/Past
// badges, reused here for Winner/Outranked so a candidate's fate reads
// the same way its own status already does.
const RESULT_PILL_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 12,
  fontWeight: 600,
  padding: '3px 9px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
};

export function WinnerPill({ won }) {
  return won
    ? <span style={{ ...RESULT_PILL_STYLE, color: '#1a7f4b', background: 'rgba(26, 127, 75, 0.12)' }}>Winner</span>
    : <span style={{ ...RESULT_PILL_STYLE, color: '#5a6b85', background: '#dde3ea' }}>Outranked</span>;
}

export function FallbackPill() {
  return <span style={{ ...RESULT_PILL_STYLE, color: '#0d5c8f', background: '#e8f4fd' }}>Fallback</span>;
}

// A row's Result is only meaningful relative to the Winner, and only for
// rows that were actually in the running for it — the winner itself, any
// other Current row that lost to it (Outranked), and Retail's own Current
// entry when it isn't itself the winner (Fallback, since every other
// Group's Price calculates from it). A Future/Past row was never in the
// running at all — nothing to call it, so it shows no Result pill.
export function CandidateResultPill({ candidate, winnerPriceId }) {
  if (candidate.id === winnerPriceId) return <WinnerPill won />;
  if (candidate.status !== 'current') return <span style={{ color: '#bbb' }}>—</span>;
  if (candidate.isAnchor) return <FallbackPill />;
  return <WinnerPill won={false} />;
}

// Shared between the active and inactive candidates tables — two separate
// <table> elements otherwise auto-size their columns independently from
// their own content, so identical logical columns (e.g. Customer) end up
// different widths and the tables don't line up when stacked.
const RESULT_TABLE_COL_WIDTHS = ['3%', '9%', '12%', '12%', '22%', '16%', '12%', '14%'];

function ResultTableColGroup() {
  return (
    <colgroup>
      {RESULT_TABLE_COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
    </colgroup>
  );
}

// A missing Retail Price is a data-configuration gap, not an application
// error (see priceResolver.js's RETAIL_PRICE_MISSING hard stop) — same
// amber "needs configuring" styling as the needsPrice banner inside
// PriceLookupResultRow and the SkipNotes box above, shown in the results
// area itself (no popup toast) since there's no partial/fallback row to
// show alongside it.
export function RetailPriceMissingBanner({ sku, uomCode, anchorGroupName, productId }) {
  return (
    <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#7a5c00', marginBottom: 16 }}>
      {sku}{uomCode ? ` / ${uomCode}` : ''} requires a {anchorGroupName} Price — Setup via{' '}
      <Link to={`/sales-prices?product_id=${productId}`} style={{ color: '#7a5c00', fontWeight: 600, textDecoration: 'underline' }}>Prices</Link>
    </div>
  );
}

// Top-of-results skip notes — same wording/gating in both the live view and
// the printed report.
// A specific Customer holds one Customer Price Group per Product Price
// Group (issue #240) — this says which one applied to the looked-up
// Product, and through which Product Price Group, or that it came from the
// Customer's Tier's default.
function AppliedCustomerPriceGroupNote({ result, customerScopeType }) {
  if (customerScopeType !== 'specific' || !result.appliedCustomerPriceGroupName) return null;
  return (
    <div style={{ background: '#eef4fb', border: '1px solid #c5d8ee', borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#2d4a6b', marginBottom: 16 }}>
      Priced as <strong>{result.appliedCustomerPriceGroupName}</strong> — {result.appliedViaTierName
        ? <>the default Customer Price Group of this Customer's Tier, <strong>{result.appliedViaTierName}</strong>.</>
        : <>this Customer's Price Group on <strong>{result.appliedViaProductPriceGroupName}</strong>.</>}
    </div>
  );
}

export function SkipNotes({ result, customerScopeType }) {
  const showGroupNote = showCustomerGroupSkipNote(customerScopeType, result);
  const appliedNote = <AppliedCustomerPriceGroupNote result={result} customerScopeType={customerScopeType} />;
  if (!(result.productPriceGroupSkippedCount > 0 || showGroupNote)) return appliedNote;
  return (
    <>
      {appliedNote}
      <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#7a5c00', marginBottom: 16 }}>
        {result.productPriceGroupSkippedCount > 0 && (
          <div>{result.productPriceGroupSkippedCount} active Price{result.productPriceGroupSkippedCount === 1 ? '' : 's'} scoped to a Product Price Group exist{result.productPriceGroupSkippedCount === 1 ? 's' : ''} tenant-wide but {result.productPriceGroupSkippedCount === 1 ? 'isn\'t' : "aren't"} evaluated here — this Product has no Product Price Group assigned (set one on the Product's own edit form to test it).</div>
        )}
        {showGroupNote && customerScopeType === 'specific' && (
          <div>This Customer has no Price Group on this Product's Product Price Group or its parents, so they're priced at Retail — {result.customerPriceGroupSkippedCount} active Price{result.customerPriceGroupSkippedCount === 1 ? '' : 's'} scoped to a Customer Price Group {result.customerPriceGroupSkippedCount === 1 ? 'isn\'t' : "aren't"} evaluated here. Add one on the Customer's Price Groups panel.</div>
        )}
        {showGroupNote && customerScopeType !== 'specific' && (
          <div>{result.customerPriceGroupSkippedCount} active Price{result.customerPriceGroupSkippedCount === 1 ? '' : 's'} scoped to a Customer Price Group exist{result.customerPriceGroupSkippedCount === 1 ? 's' : ''} tenant-wide but {result.customerPriceGroupSkippedCount === 1 ? 'isn\'t' : "aren't"} evaluated here — switch to "Customer Price Group" above and search for the specific group to test it directly.</div>
        )}
      </div>
    </>
  );
}

// The card's sections (UoM figures, Per base UoM, Margin) sit side by side
// in the one card, split by thin rules, each under a small uppercase
// heading — ui-architecture.md "Sectioned Result Card".
const SECTION_HEADING_STYLE = { fontSize: 11, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 };
const SECTION_RULE = '1px solid #e3e8ef';
// Every figure, in every section, sits on the same three fixed-height rows
// (label, amount, basis), and each amount sits on the bottom of its row, so
// the amounts and their basis lines line up across sections whatever
// their font size or whether they have a label.
const FIGURE_LABEL_STYLE = { fontSize: 12, color: '#888', height: 16, lineHeight: '16px' };
const FIGURE_VALUE_ROW_STYLE = { height: 28, display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', lineHeight: 1 };
const FIGURE_VALUE_STYLE = { fontSize: 20, fontWeight: 700 };
const BASIS_STYLE = { fontSize: 11, color: '#8a94a6', height: 16, lineHeight: '16px', marginTop: 4 };

// centred: the heading sits over the middle of a group of figures (the
// UoM section) rather than at its left edge.
function ResultSection({ heading, first, centred, children }) {
  return (
    <div style={{ padding: first ? '10px 24px 12px 0' : '10px 24px 12px', borderLeft: first ? 'none' : SECTION_RULE }}>
      <div style={{ ...SECTION_HEADING_STYLE, textAlign: centred ? 'center' : 'left' }}>{heading}</div>
      {children}
    </div>
  );
}

// basis: "ex GST" / "inc GST" under the amount itself, so the reader
// needn't look back to the toggle to know which one they're reading.
// Every amount on the card is a Figure, in the one size, weight and
// colour.
function Figure({ label = '', value, basis = '' }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={FIGURE_LABEL_STYLE}>{label}</div>
      <div style={FIGURE_VALUE_ROW_STYLE}><span style={FIGURE_VALUE_STYLE}>{value}</span></div>
      <div style={BASIS_STYLE}>{basis}</div>
    </div>
  );
}

// One resolved UoM row's full card — price figures, upsell/ambiguous/
// needs-price notes, and the Price Resolution Priority table(s). Rendered
// identically by the live page and the printed report.
// viewInc: the Ex GST / Inc GST view the page is on (view only — prices
// are resolved from stored figures either way); taxHandling: the scope's
// Pricing Basis, for rounding inc figures (see viewFigures); titleAction:
// shown at the right of the card's title line (the live page's Ex/Inc GST
// toggle — the print has none).
export function PriceLookupResultRow({ row, qty, productSearch, customerSearch, customerGroups, viewInc = false, taxHandling = null, titleAction = null }) {
  const anchorGroup = customerGroups.find((g) => g.is_system_default);
  const anchorName = anchorGroup?.name || 'Retail';
  const winnerCandidate = row.candidates.find((c) => c.id === row.winnerPriceId);
  const winnerScopeLabel = winnerCandidate ? describeCustomerScope(winnerCandidate, { customerSearch, customerGroups }) : null;
  const figures = viewFigures(row, { qty, inc: viewInc, taxHandling });
  const basis = viewInc ? 'inc GST' : 'ex GST';
  // Margin against Retail only means something once this row's winning
  // Price is actually a non-Retail one — a winner that IS Retail's own
  // Price (or no winner at all, which resolves to Retail's figure
  // unchanged per priceResolution.js's applyPriceAtQuantity) has nothing to
  // compare against itself. Margin is an ex GST figure, so Ex view only.
  const showMargin = !viewInc && !!winnerCandidate && !winnerCandidate.isAnchor
    && row.retailPrice !== null && row.retailPrice !== undefined && row.retailPrice !== 0
    && row.unitPrice !== null && row.unitPrice !== undefined;
  const marginDollar = showMargin ? row.retailPrice - row.unitPrice : null;
  const marginPercent = showMargin ? (marginDollar / row.retailPrice) * 100 : null;
  const nextBreakMarginPercent = showMargin && row.nextBreak
    ? ((row.retailPrice - row.nextBreak.unitPrice) / row.retailPrice) * 100
    : null;
  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: '14px 18px', marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, paddingBottom: 8, borderBottom: SECTION_RULE }}>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{row.uomCode} — {row.uomName}</span>
        {titleAction}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', borderBottom: SECTION_RULE, marginBottom: 10 }}>
        <ResultSection heading={row.uomCode} first centred>
          <div style={{ display: 'flex', gap: 36 }}>
            <Figure label="Unit" value={formatUnitPrice(figures.unitPrice)} basis={basis} />
            <Figure label="Extended" value={formatPrice(figures.extendedPrice)} basis={basis} />
            {viewInc && (
              <Figure label={row.gstApplicable ? `GST ${formatQty(row.gstRate)}%` : 'GST'} value={formatPrice(figures.gstAmount)} />
            )}
          </div>
        </ResultSection>
        {row.uomCode !== row.baseUomCode && (
          <ResultSection heading={`Per ${row.baseUomCode}`}>
            <Figure label={`${formatQty(row.baseQty)} ${row.baseUomCode} @`} value={formatUnitPrice(figures.basePrice)} basis={basis} />
          </ResultSection>
        )}
        {showMargin && (
          <ResultSection heading={`Margin vs ${anchorName}`}>
            <div style={{ display: 'flex', gap: 36 }}>
              <Figure label="$" value={formatPrice(marginDollar)} basis="ex GST" />
              <Figure label="%" value={formatMarginPercent(marginPercent)} />
            </div>
          </ResultSection>
        )}
      </div>

      <CalculationBreakdown breakdown={row.breakdown} anchorName={anchorName} scopeLabel={winnerScopeLabel} incPrice={viewInc ? figures.unitPrice : null} />

      {row.nextBreak && (
        <div style={{ background: '#e8f4fd', border: '1px solid #b6dcf5', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#0d5c8f', marginBottom: 10 }}>
          Upsell: at {row.nextBreak.minQuantity} {row.uomCode}, unit price drops to {formatUnitPrice(figures.nextBreakUnitPrice)} ({formatPrice(figures.nextBreakExtendedPrice)} total, {basis}){nextBreakMarginPercent !== null ? `, ${formatMarginPercent(nextBreakMarginPercent)} margin vs ${anchorName}` : ''}.
        </div>
      )}

      {row.ambiguous && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#b91c1c', marginBottom: 10 }}>
          Multiple equally-specific Prices match — no single winner could be picked. See candidates below.
        </div>
      )}
      {row.needsPrice && (
        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#7a5c00', marginBottom: 10 }}>
          The winning Fixed Override Price has no price entered yet.
        </div>
      )}

      <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.4, margin: '0 0 4px' }}>
        Price Resolution Priority — most to least specific
      </div>

      {row.candidates.length === 0 ? (
        <div style={{ fontSize: 13, color: '#888' }}>No Prices exist yet for this Product+UoM, not even a {anchorName} Price.</div>
      ) : (
        <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: reportStyle.tableFontSize }}>
          <ResultTableColGroup />
          <thead>
            <tr style={{ background: '#eef2f7' }}>
              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: reportStyle.tableFontSize, color: '#666' }}>#</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: reportStyle.tableFontSize, color: '#666' }}>Source</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: reportStyle.tableFontSize, color: '#666' }}>Customer</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: reportStyle.tableFontSize, color: '#666' }}>Product</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: reportStyle.tableFontSize, color: '#666' }}>Type</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: reportStyle.tableFontSize, color: '#666' }}>Effective</th>
              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: reportStyle.tableFontSize, color: '#666' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                  Status
                  <FieldHelp fieldKey="price-lookup.candidate-status" pageKey="price-lookup" label="Status">Current/Future/Past is relative to the As of date above, not to today's date.</FieldHelp>
                </span>
              </th>
              <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: reportStyle.tableFontSize, color: '#666' }}></th>
            </tr>
          </thead>
          <tbody>
            {row.candidates.map((c, idx) => (
              <tr key={c.id} style={{ borderTop: '1px solid #eee', background: c.id === row.winnerPriceId ? '#eaf6ea' : 'transparent' }}>
                <td style={{ padding: '6px 8px' }}>{idx + 1}</td>
                <td style={{ padding: '6px 8px' }}>{c.isAnchor ? `${anchorName} Base Price` : 'Price'}</td>
                <td style={{ padding: '6px 8px' }}>{describeCustomerScope(c, { customerSearch, customerGroups })}</td>
                <td style={{ padding: '6px 8px' }}>{describeProductScope(c, { productSearch })}</td>
                <td style={{ padding: '6px 8px' }}><AdjustmentTypeCell item={toAdjTypeItem(c.method)} /></td>
                <td style={{ padding: '6px 8px' }}>{formatDate(c.start_date) || '—'} – {formatDate(c.end_date) || '—'}</td>
                <td style={{ padding: '6px 8px' }}><StatusPill item={c} /></td>
                <td style={{ padding: '6px 8px' }}><CandidateResultPill candidate={c} winnerPriceId={row.winnerPriceId} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
